/**
 * td-plus-upgrade
 *
 * Adds the "Upgrade to Plus" switch to product pages that use the native
 * variant picker + product form (the bundle PDPs). When the switch is on,
 * Add to Cart posts a single multi-item /cart/add.js containing the product
 * variant AND the Plus upgrade product, so the upgrade lands as its own line
 * item - matching what the Vue addons app does on the ANCORE Pro PDP.
 *
 * Everything the component needs - selectors, classes, copy, the form registry
 * and the submit listener - lives on the class itself, so the only thing at
 * module scope is the registration guard.
 *
 * Markup: snippets/td-plus-upgrade-toggle.liquid
 */
if (!customElements.get('td-plus-upgrade')) {
  class TdPlusUpgrade extends HTMLElement {
    static SELECTORS = {
      toggle: '[data-td-plus-toggle]',
      section: '[data-section-type="product-template"]',
      // Vendor theme hooks below. This markup belongs to theme.js /
      // snippets/td-product-template-v2.liquid, so we match what it already
      // exposes instead of adding data-td-* hooks to vendor output.
      addToCart: '[data-add-to-cart]',
      price: '[data-product-price]',
      comparePrice: '[data-product-price-compare]',
      errors: '.errors',
      additionalButtons: '[data-td-express-checkout]',
      // Presence of the Vue addons app means it already owns a Plus toggle.
      vueAddonsApp: '#product-form-addon'
    };

    static CLASSES = {
      hidden: 'hide',
      loading: 'btn--loading',
      errors: ['errors', 'text-center']
    };

    // Last-resort only. The real copy comes from data-td-error-message so it can
    // be translated in Liquid; this exists so a failed add can never be silent.
    static FALLBACK_ERROR = 'Something went wrong. Please try again.';

    // Product form element -> the instance that owns it. Weakly held so a form
    // removed by the theme editor does not keep its component alive.
    static forms = new WeakMap();

    static isListening = false;

    /**
     * theme.AjaxProduct binds its submit handler AT TARGET on the form
     * (assets/theme.js, theme.AjaxProduct). At-target listeners run in
     * registration order regardless of the capture flag, so capturing on
     * document - the first node in the propagation path - is the only reliable
     * way to stop it.
     *
     * Bound once for the whole class rather than per instance: several instances
     * sharing one form must not each add to cart, and the class definition sits
     * inside the customElements guard, so a duplicate script tag cannot produce
     * a second registry or a second listener.
     */
    static ensureSubmitListener() {
      if (TdPlusUpgrade.isListening) return;
      TdPlusUpgrade.isListening = true;
      document.addEventListener('submit', TdPlusUpgrade.handleSubmit, true);
    }

    static handleSubmit(event) {
      const element = TdPlusUpgrade.forms.get(event.target);

      // Switch off: leave theme.AjaxProduct's path completely untouched.
      if (!element || !element.isSelected) return;

      event.preventDefault();
      event.stopPropagation();
      element.addToCartWithUpgrade(event.target);
    }

    constructor() {
      super();
      this.isSelected = false;
      this.isLoading = false;
      this.hasOverlay = false;
      this.frame = null;
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      const { SELECTORS } = TdPlusUpgrade;

      // Belt and braces: the Vue addons app renders its own Plus toggle.
      if (document.querySelector(SELECTORS.vueAddonsApp)) {
        this.disableToggle();
        return;
      }

      this.section = this.closest(SELECTORS.section);
      this.form = document.getElementById(this.dataset.tdFormId);
      this.input = this.querySelector(SELECTORS.toggle);

      if (!this.section || !this.form || !this.input) {
        this.disableToggle();
        return;
      }

      this.basePrice = Number(this.dataset.tdBasePrice) || 0;
      this.baseComparePrice = Number(this.dataset.tdBaseComparePrice) || 0;
      this.upgradePrice = Number(this.dataset.tdUpgradePrice) || 0;
      this.upgradeVariantId = Number(this.dataset.tdVariantId) || 0;
      this.upgradeQuantity = Number(this.dataset.tdQuantity) || 1;

      if (!this.upgradeVariantId) {
        this.disableToggle();
        return;
      }

      // One form, one switch. The registry holds a single instance per form, so
      // a second toggle would leave it pointing at whichever connected last -
      // flipping the other one would then fall through to theme.AjaxProduct and
      // silently drop the upgrade. First one wins; any extra goes inert before
      // it wires anything up, so the switch the customer can see always works.
      // Not reachable today (variant_picker is limit: 1, and the two render
      // sites are mutually exclusive via buy_button_rendered) - it fails closed.
      if (TdPlusUpgrade.forms.has(this.form)) {
        this.disableToggle();
        return;
      }

      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.input.addEventListener('change', () => {
        this.setSelected(this.input.checked);
      }, { signal });

      // role="switch" on a real checkbox gives Space for free. Enter matches
      // handlePlusToggleKeydown in assets/td-product-addons.js.
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.input.checked = !this.input.checked;
        this.setSelected(this.input.checked);
      }, { signal });

      // theme.js fires variantChange on the section container and variant:change
      // on document with the same payload. The document event carries no section
      // reference, so a second picker on the page (quick shop, featured product)
      // would feed us another product's prices - listen on our own section.
      this.section.addEventListener('variantChange', this.onVariantChange, { signal });

      TdPlusUpgrade.forms.set(this.form, this);
      TdPlusUpgrade.ensureSubmitListener();

      // Browsers restore checkbox state on back/forward, so read it rather
      // than assuming off.
      this.setSelected(this.input.checked);
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      // Only clear the registry if this instance is the one holding the form.
      if (this.form && TdPlusUpgrade.forms.get(this.form) === this) {
        TdPlusUpgrade.forms.delete(this.form);
      }
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    // A switch that can be flipped but does nothing would let someone order
    // without the upgrade they asked for, so take it out of play entirely.
    disableToggle() {
      this.hidden = true;
      const input = this.querySelector(TdPlusUpgrade.SELECTORS.toggle);
      if (input) input.disabled = true;
    }

    setSelected(selected) {
      this.isSelected = Boolean(selected);
      this.input.setAttribute('aria-checked', String(this.isSelected));

      // Express checkout buttons submit the form's variant straight to
      // checkout without touching this JS, which would silently drop the
      // upgrade. Hide them while the switch is on.
      const additionalButtons = this.form.querySelector(TdPlusUpgrade.SELECTORS.additionalButtons);
      if (additionalButtons) {
        additionalButtons.classList.toggle(TdPlusUpgrade.CLASSES.hidden, this.isSelected);
      }

      this.renderPrice();
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      this.basePrice = variant.price || 0;
      this.baseComparePrice = variant.compare_at_price || 0;

      // theme.Variants fires variantChange BEFORE it rewrites the price, so
      // defer a frame to land after that write instead of being clobbered.
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.renderPrice();
      });
    }

    /**
     * Overlays the upgrade onto the displayed price. Idempotent, and writes
     * once more when the switch goes off so the figure returns to exactly what
     * theme.js would have rendered. Sale state itself is untouched - the
     * upgrade applies equally to both figures, so theme.js's own sale handling
     * stays correct.
     */
    renderPrice() {
      const { SELECTORS } = TdPlusUpgrade;

      // Never touch the price until the switch has actually been used, so the
      // Liquid-rendered figure stands untouched on a normal page load.
      if (!this.isSelected && !this.hasOverlay) return;
      this.hasOverlay = this.isSelected;

      const delta = this.isSelected ? this.upgradePrice : 0;

      const priceElement = this.section.querySelector(SELECTORS.price);
      if (priceElement) {
        priceElement.textContent = this.formatMoney(this.basePrice + delta);
      }

      // Only rendered when the product has a compare-at price at all.
      const compareElement = this.section.querySelector(SELECTORS.comparePrice);
      if (compareElement && this.baseComparePrice > this.basePrice) {
        compareElement.textContent = this.formatMoney(this.baseComparePrice + delta);
      }
    }

    addToCartWithUpgrade(form) {
      if (this.isLoading) return;

      const data = new FormData(form);

      // Unavailable variants render an <option> with no value, so an empty id
      // is exactly the sold-out case.
      const id = Number(data.get('id'));
      if (!id) return;

      const quantity = Number(data.get('quantity')) || 1;
      const item = { id: id, quantity: quantity };

      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) {
        item.selling_plan = Number(sellingPlan);
      }

      // The form also carries the option radios (they opt in via form="..."),
      // so read only the fields /cart/add.js understands.
      const properties = {};
      data.forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) {
        item.properties = properties;
      }

      const items = [
        item,
        {
          id: this.upgradeVariantId,
          quantity: quantity * this.upgradeQuantity
        }
      ];

      this.setLoading(true);
      this.clearErrors();

      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: items })
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body: body })))
        .then(({ ok, body }) => {
          if (!ok || body.status === 422) {
            this.error(body);
            return;
          }
          this.success(body);
        })
        .catch(() => {
          this.error({ description: this.dataset.tdErrorMessage || TdPlusUpgrade.FALLBACK_ERROR });
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    success(cart) {
      // Guarded because an undefined route would navigate nowhere and leave the
      // button stuck in its loading state.
      if (theme.settings.cartType === 'page' && theme.routes && theme.routes.cartPage) {
        window.location = theme.routes.cartPage;
        return;
      }

      const detail = { product: cart, addToCartBtn: this.addToCartButton() };

      // Mid-add section re-renders detach the form, and an event on a detached
      // node never reaches the cart drawer.
      const target = this.form.isConnected ? this.form : document;

      // Mirrors theme.AjaxProduct: the cart drawer, sticky cart and
      // assets/td-cart.js all hang off these.
      target.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: detail,
        bubbles: true
      }));

      const scopedEventId = this.scopedEventId();
      if (scopedEventId) {
        document.dispatchEvent(new CustomEvent(`ajaxProduct:added:${scopedEventId}`, { detail: detail }));
      }
    }

    error(error) {
      const message = typeof error.description === 'object' ? error.message : error.description;

      if (!message) {
        console.warn(error);
        return;
      }

      this.clearErrors();

      const errorDiv = document.createElement('div');
      errorDiv.classList.add(...TdPlusUpgrade.CLASSES.errors);
      errorDiv.textContent = message;
      this.form.append(errorDiv);

      const detail = { errorMessage: message };

      this.form.dispatchEvent(new CustomEvent('ajaxProduct:error', {
        detail: detail,
        bubbles: true
      }));

      const scopedEventId = this.scopedEventId();
      if (scopedEventId) {
        document.dispatchEvent(new CustomEvent(`ajaxProduct:error:${scopedEventId}`, { detail: detail }));
      }
    }

    clearErrors() {
      const errors = this.form.querySelector(TdPlusUpgrade.SELECTORS.errors);
      if (errors) errors.remove();
    }

    setLoading(loading) {
      this.isLoading = loading;

      const button = this.addToCartButton();
      if (button) button.classList.toggle(TdPlusUpgrade.CLASSES.loading, loading);
    }

    addToCartButton() {
      return this.form.querySelector(TdPlusUpgrade.SELECTORS.addToCart);
    }

    // Matches the scopedEventId theme.Product hands theme.AjaxProduct, so the
    // sticky cart and product screens keep working.
    scopedEventId() {
      const sectionId = this.section.dataset.sectionId;
      return sectionId ? `ProductScreen-${sectionId}` : null;
    }

    formatMoney(value) {
      return theme.Currency.formatMoney(value, theme.settings.moneyFormat);
    }
  }

  customElements.define('td-plus-upgrade', TdPlusUpgrade);
}
